import { useCallback } from "react";
import { ChatApiClient } from "@/lib/api/clients/chat-api-client";
import { toast } from "@/hooks/use-toast";
import type {
  ChatSession,
  ListChatSessionsParams,
  ChatSessionUpdateRequestBody,
  SessionsDeletedResponse,
} from "@/lib/types/api/chat-types";
import type { PaginatedResponse } from "@/lib/types/api/common-types";

/**
 * Chat Service Hook
 *
 * Provides access to chat-related operations with automatic error handling
 * and user feedback via toast notifications.
 *
 * @returns Object containing chat service methods
 */
export function useChatService() {
  const chatApiClient = new ChatApiClient();

  /**
   * List chat sessions for an agent
   * @param agentId - The agent ID
   * @param params - Optional pagination and filtering parameters
   * @returns Promise<PaginatedResponse<ChatSession> | null>
   */
  const listChatSessions = useCallback(
    async (
      agentId: string,
      params?: ListChatSessionsParams
    ): Promise<PaginatedResponse<ChatSession> | null> => {
      try {
        const sessions = await chatApiClient.listChatSessions(agentId, params);
        return sessions;
      } catch (error) {
        console.error("Failed to fetch chat sessions:", error);
        toast({
          title: "Failed to load sessions",
          description: "Unable to fetch chat sessions. Please try again.",
          variant: "destructive",
        });
        return null;
      }
    },
    []
  );

  /**
   * Update a chat session (e.g., rename)
   * @param agentId - The agent ID
   * @param sessionId - The session ID
   * @param data - Update data (e.g., new name)
   * @returns Promise<boolean> - Success status
   */
  const updateChatSession = useCallback(
    async (
      agentId: string,
      sessionId: string,
      data: ChatSessionUpdateRequestBody
    ): Promise<boolean> => {
      try {
        await chatApiClient.updateChatSession(agentId, sessionId, data);
        toast({
          title: "Success",
          description: "Session updated successfully",
        });
        return true;
      } catch (error) {
        console.error("Failed to update chat session:", error);
        toast({
          title: "Failed to update session",
          description: "Unable to update the session. Please try again.",
          variant: "destructive",
        });
        return false;
      }
    },
    []
  );

  /**
   * Delete a specific chat session
   * @param agentId - The agent ID
   * @param sessionId - The session ID
   * @returns Promise<boolean> - Success status
   */
  const deleteChatSession = useCallback(
    async (agentId: string, sessionId: string): Promise<boolean> => {
      try {
        await chatApiClient.deleteChatSession(agentId, sessionId);
        toast({
          title: "Success",
          description: "Session deleted successfully",
        });
        return true;
      } catch (error) {
        console.error("Failed to delete chat session:", error);
        toast({
          title: "Failed to delete session",
          description: "Unable to delete the session. Please try again.",
          variant: "destructive",
        });
        return false;
      }
    },
    []
  );

  /**
   * Delete all chat sessions for an agent
   * @param agentId - The agent ID
   * @returns Promise<SessionsDeletedResponse | null>
   */
  const deleteAllChatSessions = useCallback(
    async (agentId: string): Promise<SessionsDeletedResponse | null> => {
      try {
        const response = await chatApiClient.deleteAllChatSessions(agentId);
        toast({
          title: "Success",
          description:
            response.message || `Deleted ${response.deleted_count} sessions`,
        });
        return response;
      } catch (error) {
        console.error("Failed to delete all chat sessions:", error);
        toast({
          title: "Failed to delete sessions",
          description: "Unable to delete all sessions. Please try again.",
          variant: "destructive",
        });
        return null;
      }
    },
    []
  );

  return {
    // Session operations
    listChatSessions,
    updateChatSession,
    deleteChatSession,
    deleteAllChatSessions,
  };
}
